import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

export const CustomizationOptionsEditor = ({ options, onChange }) => {
  const [expanded, setExpanded] = useState(null);

  const addOption = () => {
    const newOption = {
      id: `opt_${Date.now()}`,
      name: 'New Option',
      type: 'radio',
      required: true,
      sequence: options.length,
      choices: []
    };
    onChange([...options, newOption]);
    setExpanded(newOption.id);
  };

  const updateOption = (id, updates) => {
    onChange(options.map(opt => opt.id === id ? { ...opt, ...updates } : opt));
  };

  const deleteOption = (id) => {
    onChange(options.filter(opt => opt.id !== id));
  };

  const moveOption = (id, direction) => {
    const index = options.findIndex(opt => opt.id === id);
    if (direction === 'up' && index > 0) {
      const newOptions = [...options];
      [newOptions[index - 1], newOptions[index]] = [newOptions[index], newOptions[index - 1]];
      newOptions.forEach((opt, idx) => opt.sequence = idx);
      onChange(newOptions);
    } else if (direction === 'down' && index < options.length - 1) {
      const newOptions = [...options];
      [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
      newOptions.forEach((opt, idx) => opt.sequence = idx);
      onChange(newOptions);
    }
  };

  const addChoice = (optionId) => {
    const option = options.find(opt => opt.id === optionId);
    const newChoice = { name: 'New Choice', price: 0 };
    updateOption(optionId, {
      choices: [...option.choices, newChoice]
    });
  };

  const updateChoice = (optionId, choiceIndex, updates) => {
    const option = options.find(opt => opt.id === optionId);
    const newChoices = [...option.choices];
    newChoices[choiceIndex] = { ...newChoices[choiceIndex], ...updates };
    updateOption(optionId, { choices: newChoices });
  };

  const deleteChoice = (optionId, choiceIndex) => {
    const option = options.find(opt => opt.id === optionId);
    updateOption(optionId, {
      choices: option.choices.filter((_, idx) => idx !== choiceIndex)
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-base font-semibold">Customization Options</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addOption}
          className="h-8"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Option
        </Button>
      </div>

      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center border-2 border-dashed rounded-lg">
          No customization options. Click "Add Option" to create one.
        </p>
      ) : (
        <div className="space-y-3">
          {options.map((option, index) => (
            <div key={option.id} className="border rounded-lg p-4 bg-secondary/20">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 mt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveOption(option.id, 'up')}
                    disabled={index === 0}
                  >
                    <MoveUp className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => moveOption(option.id, 'down')}
                    disabled={index === options.length - 1}
                  >
                    <MoveDown className="h-3 w-3" />
                  </Button>
                </div>

                <div className="flex-1 space-y-3">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Option name (e.g., Choose your sauce)"
                        value={option.name}
                        onChange={(e) => updateOption(option.id, { name: e.target.value })}
                        className="h-9"
                      />
                    </div>
                    <Select
                      value={option.type}
                      onValueChange={(value) => updateOption(option.id, { type: value })}
                    >
                      <SelectTrigger className="w-32 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="radio">Single Choice</SelectItem>
                        <SelectItem value="checkbox">Multiple</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={option.required ? 'required' : 'optional'}
                      onValueChange={(value) => updateOption(option.id, { required: value === 'required' })}
                    >
                      <SelectTrigger className="w-32 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="required">Required</SelectItem>
                        <SelectItem value="optional">Optional</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-red-600"
                      onClick={() => deleteOption(option.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {expanded === option.id && (
                    <div className="space-y-2 pl-4 border-l-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm">Choices</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addChoice(option.id)}
                          className="h-7"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Choice
                        </Button>
                      </div>
                      {option.choices.map((choice, choiceIndex) => (
                        <div key={choiceIndex} className="flex gap-2">
                          <Input
                            placeholder="Choice name"
                            value={choice.name}
                            onChange={(e) => updateChoice(option.id, choiceIndex, { name: e.target.value })}
                            className="h-8"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price"
                            value={choice.price}
                            onChange={(e) => updateChoice(option.id, choiceIndex, { price: parseFloat(e.target.value) || 0 })}
                            className="w-24 h-8"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={() => deleteChoice(option.id, choiceIndex)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded(expanded === option.id ? null : option.id)}
                    className="h-7 text-xs"
                  >
                    {expanded === option.id ? 'Hide' : 'Show'} Choices ({option.choices.length})
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
